import { Store } from 'vuex'
import { optionLabel } from '@vue-storefront/core/modules/catalog/helpers'

const MAX_ECOMMERCE_ITEMS = 200
const VARIANT_ATTRIBUTE_CODES = ['color', 'size', 'dekor', 'farbton']

export interface Ga4Item {
  item_id: string,
  item_name: string,
  item_brand?: string,
  item_category?: string,
  item_variant?: string,
  price: number,
  quantity: number,
  index?: number,
  item_list_id?: string,
  item_list_name?: string
}

export interface Ga4ItemListContext {
  item_list_id?: string,
  item_list_name?: string
}

export function roundMoney (value: number): number {
  return Math.round((Number(value) || 0) * 100) / 100
}

function toNumber (value: any): number | null {
  if (value === null || value === undefined || value === '') return null
  const num = Number(value)
  return Number.isFinite(num) ? num : null
}

function coalesce<T> (value: T | null | undefined, fallback: T): T {
  return (value === undefined || value === null) ? fallback : value
}

export function getGa4ItemPrice (item: any): number {
  const raw = (item && (
    item.priceInclTax ||
    item.price_incl_tax ||
    (item.totals && item.totals.price_incl_tax) ||
    item.final_price ||
    item.specialPrice ||
    item.regularPrice ||
    item.price
  )) || 0

  return roundMoney(coalesce(toNumber(raw), 0))
}

export function getGa4ItemQuantity (item: any, specifiedQty?: number | null): number {
  const raw = specifiedQty !== null && specifiedQty !== undefined
    ? specifiedQty
    : (item && item.qty != null ? item.qty : 1)
  const qty = parseInt(String(raw), 10)
  return Number.isFinite(qty) && qty > 0 ? qty : 1
}

function isPlainLabel (value: any): boolean {
  return typeof value === 'string' && value.trim() !== '' && Number.isNaN(Number(value))
}

function resolveAttributeLabel (store: Store<any>, attributeCode: string, optionId: any): string | undefined {
  if (optionId === undefined || optionId === null || optionId === '') return undefined
  if (isPlainLabel(optionId)) return String(optionId).trim()

  const attributeState = store?.state?.attribute
  if (!attributeState) return undefined

  const label = optionLabel(attributeState, { attributeKey: attributeCode, optionId })
  if (!label || String(label) === String(optionId)) return undefined

  return String(label)
}

function getItemBrand (item: any, store: Store<any>): string | undefined {
  return resolveAttributeLabel(store, 'marke', item?.marke)
}

function getItemCategory (item: any, fallback?: string): string | undefined {
  if (Array.isArray(item?.category) && item.category.length > 0) {
    const named = [...item.category].reverse().find(category => category && category.name)
    if (named?.name) return named.name
  }

  if (item?.category?.name) return item.category.name
  if (item?.item_category) return item.item_category

  return fallback || undefined
}

function getItemVariant (item: any, store: Store<any>): string | undefined {
  const parts: string[] = []

  VARIANT_ATTRIBUTE_CODES.forEach(code => {
    const label = resolveAttributeLabel(store, code, item?.[code])
    if (label) parts.push(label)
  })

  if (!parts.length) {
    const cartOptions = item?.options || item?.totals?.options
    const optionList = Array.isArray(cartOptions)
      ? cartOptions
      : (cartOptions && typeof cartOptions === 'object' ? Object.values(cartOptions) : [])

    optionList.forEach((option: any) => {
      const value = option?.value || option?.option_value
      if (typeof value === 'string' && value.trim()) {
        parts.push(value.trim())
      }
    })
  }

  const unique = Array.from(new Set(parts.filter(Boolean)))
  return unique.length ? unique.join(' / ') : undefined
}

export function mapGa4Item (
  item: any,
  store: Store<any>,
  extras: Partial<Ga4Item> = {},
  specifiedQty?: number | null
): Ga4Item {
  const mapped: Ga4Item = {
    item_id: String(item?.sku || item?.id || ''),
    item_name: String(item?.name || item?.title || ''),
    price: coalesce(extras.price, getGa4ItemPrice(item)),
    quantity: coalesce(extras.quantity, getGa4ItemQuantity(item, specifiedQty))
  }

  const brand = extras.item_brand || getItemBrand(item, store)
  if (brand) mapped.item_brand = brand

  const category = getItemCategory(item, extras.item_category || extras.item_list_name)
  if (category) mapped.item_category = category

  const variant = extras.item_variant || getItemVariant(item, store)
  if (variant) mapped.item_variant = variant

  if (typeof extras.index === 'number') mapped.index = extras.index
  if (extras.item_list_id) mapped.item_list_id = String(extras.item_list_id)
  if (extras.item_list_name) mapped.item_list_name = extras.item_list_name

  return mapped
}

export function mapGa4Items (
  items: any[] = [],
  store: Store<any>,
  listContext: Ga4ItemListContext = {},
  startIndex = 0
): Ga4Item[] {
  return items
    .slice(0, MAX_ECOMMERCE_ITEMS)
    .map((item, index) => mapGa4Item(item, store, {
      ...listContext,
      index: startIndex + index
    }))
}

export function getItemsValue (items: Ga4Item[] = []): number {
  return roundMoney(items.reduce((sum, item) => sum + (item.price * item.quantity), 0))
}

export function getItemListContext (store: Store<any>): Ga4ItemListContext {
  if (store?.state?.ui?.searchpanel) {
    return {
      item_list_id: 'search',
      item_list_name: 'Search'
    }
  }

  const category = store.getters['category-next/getCurrentCategory']
  if (category?.id) {
    return {
      item_list_id: String(category.id),
      item_list_name: category.name
    }
  }

  const routeName = store.state?.route?.name
  if (routeName === 'home') {
    return {
      item_list_id: 'home',
      item_list_name: 'Home'
    }
  }

  return {}
}

export function getCartValue (state: any, items: Ga4Item[] = []): number {
  const totals = state && state.cart && state.cart.platformTotals
  const grandTotal = coalesce(toNumber(totals && totals.grand_total), toNumber(totals && totals.base_grand_total))
  if (grandTotal !== null) return roundMoney(grandTotal)
  return getItemsValue(items)
}

export function buildEcommercePayload (
  currency: string,
  items: Ga4Item[],
  extra: Record<string, any> = {}
) {
  return {
    currency,
    value: extra.value != null ? extra.value : getItemsValue(items),
    ...extra,
    items
  }
}

export function trackEcommerce (GTM: any, event: string, ecommerce: Record<string, any>) {
  const dataLayer = (window as any).dataLayer
  if (Array.isArray(dataLayer)) {
    dataLayer.push({ ecommerce: null })
  }

  GTM.trackEvent({
    event,
    ecommerce
  })
}
